import { visit, Kind, OperationTypeNode, isObjectType, isInterfaceType, getNamedType } from "graphql";
import { fieldSegment, inlineFragmentSegment } from "./ast-path.js";
function readVariables(text) {
  if (!text || !text.trim()) {
    return {};
  }
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}
function pathWithinSelections(rootSelections, offset) {
  var _a;
  const path = [];
  let selections = rootSelections;
  while (true) {
    const selection = selections.find((s) => (s.kind === Kind.FIELD || s.kind === Kind.INLINE_FRAGMENT) && s.loc != null && s.loc.start <= offset && s.loc.end >= offset);
    if (!selection) {
      break;
    }
    if (selection.kind === Kind.FIELD) {
      path.push(fieldSegment(selection.name.value));
    } else {
      const typeName = (_a = selection.typeCondition) == null ? void 0 : _a.name.value;
      if (!typeName) {
        break;
      }
      path.push(inlineFragmentSegment(typeName));
    }
    if (!selection.selectionSet) {
      break;
    }
    selections = selection.selectionSet.selections;
  }
  return path;
}
function cursorContextAtOffset(doc, offset) {
  var _a;
  const def = doc.definitions.find((d) => (d.kind === Kind.OPERATION_DEFINITION || d.kind === Kind.FRAGMENT_DEFINITION) && d.loc != null && d.loc.start <= offset && d.loc.end >= offset);
  if ((def == null ? void 0 : def.kind) === Kind.OPERATION_DEFINITION) {
    return {
      target: {
        kind: "operation",
        name: (_a = def.name) == null ? void 0 : _a.value
      },
      path: pathWithinSelections(def.selectionSet.selections, offset)
    };
  }
  if ((def == null ? void 0 : def.kind) === Kind.FRAGMENT_DEFINITION) {
    return {
      target: {
        kind: "fragment",
        name: def.name.value
      },
      path: pathWithinSelections(def.selectionSet.selections, offset)
    };
  }
  return void 0;
}
function findOperationDefinition(doc, operationName) {
  const byName = operationName ? doc.definitions.find((d) => {
    var _a;
    return d.kind === Kind.OPERATION_DEFINITION && ((_a = d.name) == null ? void 0 : _a.value) === operationName;
  }) : void 0;
  const op = byName ?? doc.definitions.find((d) => d.kind === Kind.OPERATION_DEFINITION);
  return (op == null ? void 0 : op.kind) === Kind.OPERATION_DEFINITION ? op : void 0;
}
function selectionMatchesSegment(selection, segment) {
  var _a;
  switch (segment.kind) {
    case "inlineFragment":
      return selection.kind === Kind.INLINE_FRAGMENT && ((_a = selection.typeCondition) == null ? void 0 : _a.name.value) === segment.typeName;
    case "field":
      return selection.kind === Kind.FIELD && selection.name.value === segment.name;
  }
}
function extractRawArgValue(doc, path, argName, operationName) {
  const op = findOperationDefinition(doc, operationName);
  if (!op) {
    return "";
  }
  let selections = op.selectionSet.selections;
  for (let i = 0; i < path.length; i++) {
    const seg = path[i];
    const node = selections.find((s) => selectionMatchesSegment(s, seg));
    if (!node) {
      return "";
    }
    if (i === path.length - 1) {
      if (node.kind !== Kind.FIELD) {
        return "";
      }
      const argNode = (node.arguments ?? []).find((a) => a.name.value === argName);
      if (!argNode) {
        return "";
      }
      const v = argNode.value;
      if (v.kind === Kind.STRING) {
        return `"${v.value}"`;
      }
      if (v.kind === Kind.INT || v.kind === Kind.FLOAT) {
        return v.value;
      }
      if (v.kind === Kind.BOOLEAN) {
        return String(v.value);
      }
      if (v.kind === Kind.ENUM) {
        return v.value;
      }
      return "";
    }
    if (!node.selectionSet) {
      return "";
    }
    selections = node.selectionSet.selections;
  }
  return "";
}
function countSelectedFields(doc, operationName) {
  let count = 0;
  const op = findOperationDefinition(doc, operationName);
  if (op) {
    visit(op, {
      Field() {
        count++;
      }
    });
  }
  return count;
}
function resolveSchemaArg(schema, activeOpKind, path, argName) {
  const rootType = schema.getRootType(activeOpKind ?? OperationTypeNode.QUERY) ?? schema.getQueryType();
  return resolveSchemaArgFromRoot(schema, rootType ?? void 0, path, argName);
}
function resolveSchemaArgFromRoot(schema, rootType, path, argName) {
  if (!rootType || path.length === 0) {
    return;
  }
  let currentType = rootType;
  let targetField;
  for (const seg of path) {
    if (seg.kind === "inlineFragment") {
      const condition = schema.getType(seg.typeName);
      if (!condition) {
        return;
      }
      currentType = condition;
      targetField = void 0;
      continue;
    }
    if (!(isObjectType(currentType) || isInterfaceType(currentType))) {
      return;
    }
    const fields = currentType.getFields();
    const f = fields[seg.name];
    if (!f) {
      return;
    }
    targetField = f;
    const named = getNamedType(f.type);
    if (named && (isObjectType(named) || isInterfaceType(named))) {
      currentType = named;
    }
  }
  return targetField == null ? void 0 : targetField.args.find((a) => a.name === argName);
}
export {
  countSelectedFields,
  cursorContextAtOffset,
  extractRawArgValue,
  readVariables,
  resolveSchemaArg,
  resolveSchemaArgFromRoot
};
//# sourceMappingURL=schema-walk.js.map
